import React from 'react';
import { Box, Typography } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { useTranslation } from 'react-i18next';
import { authBackground, authGlassCardSx } from './authStyles';
import Logo from '../common/Logo';
import { GnssRover, TotalStation } from './SurveyInstruments';
import LanguageSelect from '../common/LanguageSelect';

// Layout for the logged-out screens, same structure as Plankton CRM's AuthShell:
// a light hero with floating frosted cards on the left, the form card on the right.

const glassCard = {
    position: 'absolute',
    borderRadius: '22px',
    background: 'rgba(255,255,255,0.75)',
    border: '1px solid rgba(255,255,255,0.9)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    boxShadow: '0 24px 60px -28px rgba(30,58,82,0.28), 0 2px 6px rgba(16,42,66,0.05)',
};

const iconTile = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, borderRadius: '10px',
    background: 'linear-gradient(135deg, rgba(66,120,152,0.16), rgba(66,120,152,0.06))',
    border: '1px solid rgba(66,120,152,0.14)',
    color: '#427898', flexShrink: 0,
};

const demoJobs = [
    { time: '08:00', key: 'auth.hero.demo1', color: '#2EC4B6' },
    { time: '10:30', key: 'auth.hero.demo2', color: '#427898' },
    { time: '14:00', key: 'auth.hero.demo3', color: '#F4A261' },
];

function AuthHero() {
    const { t } = useTranslation();
    return (
        <Box sx={{
            display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'space-between',
            width: '56%', position: 'relative', overflow: 'hidden', px: 8, py: 7,
            '@keyframes floatA': { '0%, 100%': { transform: 'translateY(0) rotate(-6deg)' }, '50%': { transform: 'translateY(-14px) rotate(-6deg)' } },
            '@keyframes floatB': { '0%, 100%': { transform: 'translateY(0) rotate(5deg)' }, '50%': { transform: 'translateY(-10px) rotate(5deg)' } },
            '@keyframes floatC': { '0%, 100%': { transform: 'translate(-50%, 0) rotate(-1.5deg)' }, '50%': { transform: 'translate(-50%, -9px) rotate(-1.5deg)' } },
            '@keyframes floatChip': { '0%, 100%': { transform: 'translateY(0) rotate(3deg)' }, '50%': { transform: 'translateY(-7px) rotate(3deg)' } },
            '@media (prefers-reduced-motion: reduce)': { '& [data-float]': { animation: 'none !important' } },
        }}>
            <Box sx={{
                position: 'absolute', top: '38%', left: '50%', transform: 'translate(-50%, -50%)',
                width: 640, height: 640, borderRadius: '50%', pointerEvents: 'none',
                background: 'radial-gradient(circle, rgba(66,120,152,0.10) 0%, rgba(66,120,152,0.04) 40%, transparent 68%)',
            }} />

            <Box sx={{ position: 'relative', zIndex: 2 }}><Logo size={38} /></Box>

            <Box sx={{ position: 'relative', zIndex: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Scaled down on narrower hero widths so the instruments and cards keep their composition. */}
                <Box sx={{ position: 'relative', width: 700, height: 400, flexShrink: 0, transform: { md: 'scale(0.52)', lg: 'scale(0.78)', xl: 'scale(0.92)' } }}>
                    {/* Ground line, total station (left) and GNSS rover (right) framing the app cards. */}
                    <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 2, borderTop: '1.5px dashed rgba(66,120,152,0.28)' }} />
                    <Box sx={{ position: 'absolute', left: 0, bottom: 0 }}><TotalStation width={130} /></Box>
                    <Box sx={{ position: 'absolute', right: 4, bottom: 0 }}><GnssRover width={100} /></Box>
                    <Box sx={{ position: 'absolute', left: 125, right: 115, top: 0, bottom: 0 }}>
                    {/* Back card: map with surveyed parcels */}
                    <Box data-float sx={{ ...glassCard, left: 0, top: 30, width: 236, height: 170, p: 2, opacity: 0.8, animation: 'floatA 8s ease-in-out infinite' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{ ...iconTile, width: 26, height: 26 }}><PlaceOutlinedIcon sx={{ fontSize: 15 }} /></Box>
                            <Box sx={{ height: 8, width: 90, borderRadius: 4, background: 'rgba(66,120,152,0.18)' }} />
                        </Box>
                        <svg width="200" height="100" viewBox="0 0 200 100">
                            <path d="M0 70 Q50 40 100 60 T200 45" stroke="rgba(66,120,152,0.25)" strokeWidth="2" fill="none" />
                            <path d="M0 30 Q60 55 120 25 T200 20" stroke="rgba(66,120,152,0.18)" strokeWidth="2" fill="none" />
                            <polygon points="30,20 80,14 88,50 36,58" fill="rgba(239,68,68,0.18)" stroke="#EF4444" strokeWidth="1.5" />
                            <polygon points="110,52 160,46 168,86 114,90" fill="rgba(239,68,68,0.18)" stroke="#EF4444" strokeWidth="1.5" />
                            <polygon points="96,10 140,8 146,34 100,38" fill="rgba(66,120,152,0.12)" stroke="rgba(66,120,152,0.5)" strokeWidth="1.2" strokeDasharray="3 2" />
                        </svg>
                    </Box>

                    {/* Back card: PDF archive */}
                    <Box data-float sx={{ ...glassCard, right: 0, top: 60, width: 220, p: 2.25, opacity: 0.85, animation: 'floatB 9s ease-in-out infinite 0.4s' }}>
                        {[0.95, 0.78, 0.6].map((o, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: i < 2 ? 1.5 : 0, opacity: o }}>
                                <Box sx={{ ...iconTile, width: 28, height: 28, color: '#EF4444', background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.16)' }}>
                                    <PictureAsPdfOutlinedIcon sx={{ fontSize: 15 }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ height: 7, width: `${82 - i * 14}%`, borderRadius: 4, background: 'rgba(66,120,152,0.28)', mb: 0.75 }} />
                                    <Box sx={{ height: 6, width: `${50 - i * 8}%`, borderRadius: 4, background: 'rgba(66,120,152,0.14)' }} />
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Front card: today's field jobs */}
                    <Box data-float sx={{
                        ...glassCard, left: '50%', top: 128, width: 320, p: 2.75,
                        background: 'rgba(255,255,255,0.93)', border: '1px solid #fff',
                        boxShadow: '0 40px 80px -30px rgba(30,58,82,0.32), 0 2px 8px rgba(16,42,66,0.06)',
                        animation: 'floatC 7s ease-in-out infinite',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                            <Box sx={iconTile}><CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} /></Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>{t('auth.hero.todayCard')}</Typography>
                            <Typography sx={{ ml: 'auto', fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>3</Typography>
                        </Box>
                        {demoJobs.map((j) => (
                            <Box key={j.time} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.9, borderTop: '1px solid #F1F5F9' }}>
                                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#94A3B8', width: 38 }}>{j.time}</Typography>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: j.color, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12.5, color: '#334155', fontWeight: 500 }} noWrap>{t(j.key)}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Chip */}
                    <Box data-float sx={{
                        ...glassCard, right: 10, top: 10, display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1.1,
                        background: 'rgba(255,255,255,0.95)', animation: 'floatChip 6.5s ease-in-out infinite 0.2s',
                    }}>
                        <Box sx={{ ...iconTile, width: 26, height: 26 }}><BoltOutlinedIcon sx={{ fontSize: 15 }} /></Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap' }}>{t('auth.hero.chip')}</Typography>
                    </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 470 }}>
                <Typography sx={{ fontSize: 34, fontWeight: 600, letterSpacing: -0.8, lineHeight: 1.15, color: '#1a2332', mb: 1.5 }}>
                    {t('auth.hero.title1')}<br />{t('auth.hero.title2')}
                </Typography>
                <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: '#64748B', mb: 3.5, maxWidth: 420 }}>
                    {t('auth.hero.subtitle')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                    {[
                        { icon: <LockOutlinedIcon sx={{ fontSize: 14 }} />, label: t('auth.hero.pillPrivate') },
                        { icon: <FolderOutlinedIcon sx={{ fontSize: 14 }} />, label: t('auth.hero.pillArchive') },
                        { icon: <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />, label: t('auth.hero.pillPlanner') },
                    ].map((f) => (
                        <Box key={f.label} sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: 999,
                            background: 'rgba(66,120,152,0.08)', border: '1px solid rgba(66,120,152,0.16)', color: '#427898',
                        }}>
                            {f.icon}
                            <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>{f.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export default function AuthShell({ children, formMaxWidth = 400 }) {
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', background: authBackground, position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 5 }}><LanguageSelect variant="light" /></Box>
            <AuthHero />
            <Box sx={{
                flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                px: { xs: 2, sm: 6, md: 8 }, overflowY: 'auto',
                animation: 'fadeSlideIn 0.4s ease both',
                '@keyframes fadeSlideIn': { from: { opacity: 0, transform: 'translateY(16px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
            }}>
                <Box sx={{ width: '100%', maxWidth: formMaxWidth, py: 4 }}>
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
                        <Logo size={34} />
                    </Box>
                    <Box sx={authGlassCardSx}>{children}</Box>
                </Box>
            </Box>
        </Box>
    );
}
